import { LinearGradient } from 'expo-linear-gradient';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import ArrowLeft from 'lucide-react-native/icons/arrow-left';
import Clock from 'lucide-react-native/icons/clock';
import ExternalLink from 'lucide-react-native/icons/external-link';
import MapPin from 'lucide-react-native/icons/map-pin';
import Star from 'lucide-react-native/icons/star';
import { ScrollView, StyleSheet, Text, View, Pressable } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { RelatedList } from '@/components/detail/related-list';
import { TourHead } from '@/components/detail/tour-head';
import { FiltersBar } from '@/components/riviera/filters-bar';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { TourCard } from '@/components/riviera/tour-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Fonts, Radius, Spacing } from '@/constants/theme';
import { FiltersProvider } from '@/hooks/use-filters';
import { useRelatedTours } from '@/hooks/use-related';
import { useTheme } from '@/hooks/use-theme';
import { useTour } from '@todo/core/hooks/use-tour';
import { cities } from '@/lib/events-data';
import { currencySymbol } from '@todo/core/i18n/format';
import { localizedTourDescription, localizedTourTitle } from '@todo/core/i18n/tour-content';
import { useLocale } from '@todo/core/i18n/use-locale';
import { openExternal } from '@todo/core/open-external';
import { getPrefetched } from '@todo/core/static-prefetch';
import { isTourDelisted, sizedTourImage, type Tour } from '@todo/core/tours';

import css from '@/components/detail/detail-desktop.module.css';

export { generateTourStaticParams as generateStaticParams } from '@todo/core/detail/tour-detail';

type Translate = (key: string, options?: Record<string, unknown>) => string;
type Theme = ReturnType<typeof useTheme>;

function formatPrice(min: number | null, currency: string | null, t: Translate) {
  if (min == null) return '';
  const symbol = currencySymbol(currency);
  return t('common.fromPrice', { price: `${symbol}${min}` });
}

function MetaList({
  tour,
  cityName,
  theme,
  t,
}: {
  tour: Tour;
  cityName: string | null;
  theme: Theme;
  t: Translate;
}) {
  return (
    <View style={styles.metaList}>
      {tour.rating != null ? (
        <View style={styles.metaRow}>
          <Star size={16} color={theme.gold} fill={theme.gold} />
          <Text style={[styles.metaText, { color: theme.text }]}>
            {tour.rating.toFixed(1)}
            {tour.review_count != null
              ? ` · ${t('tours.reviews', { count: tour.review_count })}`
              : ''}
          </Text>
        </View>
      ) : null}
      {tour.duration ? (
        <View style={styles.metaRow}>
          <Clock size={16} color={theme.gold} />
          <Text style={[styles.metaText, { color: theme.text }]}>{tour.duration}</Text>
        </View>
      ) : null}
      {cityName ? (
        <View style={styles.metaRow}>
          <MapPin size={16} color={theme.gold} />
          <Text style={[styles.metaText, { color: theme.text }]}>{cityName}</Text>
        </View>
      ) : null}
    </View>
  );
}

function PriceRow({ tour, theme, t }: { tour: Tour; theme: Theme; t: Translate }) {
  if (tour.from_price == null) return null;
  return (
    <View style={styles.priceRow}>
      <Text style={[styles.price, { color: theme.gold }]}>
        {formatPrice(tour.from_price, tour.currency, t)}
      </Text>
      <Text style={[styles.perPerson, { color: theme.mutedForeground }]}>
        {t('tours.perPerson')}
      </Text>
    </View>
  );
}

// Shown in place of the price + Book button for a tour Viator no longer sells
// (see isTourDelisted) — avoids sending a click to a dead affiliate link and
// points at the "more tours" list below instead. Mirrors the boat screen's.
function DelistedNotice({ theme, t }: { theme: Theme; t: Translate }) {
  return (
    <View style={[styles.delistedNotice, { backgroundColor: theme.muted }]}>
      <Text style={[styles.delistedBanner, { color: theme.text }]}>{t('tours.delistedBanner')}</Text>
      <Text style={[styles.delistedHint, { color: theme.mutedForeground }]}>
        {t('tours.delistedHint')}
      </Text>
    </View>
  );
}

function BookButton({ tour, theme, t }: { tour: Tour; theme: Theme; t: Translate }) {
  const { locale } = useLocale();
  if (!tour.booking_url) return null;
  return (
    <>
      <Button
        variant="gold"
        fullWidth
        style={styles.bookBtn}
        leadingIcon={<ExternalLink size={18} color={theme.goldForeground} />}
        onPress={() =>
          openExternal(tour.booking_url as string, {
            kind: 'tour',
            source: tour.source,
            id: tour.id,
            title: localizedTourTitle(tour, locale),
            city: tour.city_id,
            locale,
          })
        }>
        {t('tours.book')}
      </Button>
      <Text style={[styles.via, { color: theme.mutedForeground }]}>
        {t('tours.viaViator')}
      </Text>
    </>
  );
}

function Hero({
  tour,
  title,
  theme,
  onBack,
  t,
}: {
  tour: Tour;
  title: string;
  theme: Theme;
  onBack: () => void;
  t: Translate;
}) {
  return (
    <>
      {tour.image_url ? (
        // Raw <img> (not expo-image) so expo's static export bakes the hero —
        // the page's LCP element — into the server-rendered HTML instead of
        // waiting for hydration to insert it. See hero-search.web.tsx.
        <img
          // Caps the request at the hero's actual display size instead of
          // shipping whatever the largest Viator variant happens to be — see
          // sizedTourImage() in tours.ts.
          src={sizedTourImage(tour.image_url, 1600, 900) ?? undefined}
          alt={title}
          className={css.heroImg}
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      ) : (
        <View style={[styles.heroFill, { backgroundColor: theme.muted }]} />
      )}
      <LinearGradient colors={['rgba(0,0,0,0.45)', 'rgba(0,0,0,0)']} style={styles.heroScrim} />
      <Pressable
        accessibilityLabel={t('a11y.back')}
        onPress={onBack}
        style={({ pressed }) => [styles.backBtn, pressed && { opacity: 0.7 }]}>
        <ArrowLeft size={20} color="#fff" />
      </Pressable>
      <View style={styles.heroBadge}>
        <Badge variant="gold">{t('tours.badge')}</Badge>
      </View>
    </>
  );
}

// Web-only: one responsive tree for the mobile and desktop layouts (see the
// single-tree section of detail-desktop.module.css), resolved at first paint
// rather than the native screen's post-mount `isLg` swap. Head + static params
// are shared with the native file.
export default function TourScreen() {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { tour: fetchedTour, loading, error } = useTour(id);

  const tour = fetchedTour ?? getPrefetched<Tour>('tour', id) ?? null;

  const cityName = tour?.city_id
    ? (cities.find((c) => c.id === tour.city_id)?.name ?? tour.city_id)
    : null;
  const tourTitle = tour ? localizedTourTitle(tour, locale) : '';
  const bodyText = tour ? localizedTourDescription(tour, locale) : null;

  const { related, relatedSeedKey, seeAllHref, cityId, wine } = useRelatedTours(tour);
  const relatedHeading = wine
    ? cityId
      ? t('tours.moreWineInCity', { city: cityName })
      : t('tours.moreWine')
    : cityId
      ? t('tours.moreInCity', { city: cityName })
      : t('tours.more');

  const onBack = () => router.back();

  const description = (t2: Translate, style?: object) =>
    bodyText ? (
      <View style={style}>
        <Text
          role="heading"
          aria-level={2}
          style={[styles.sectionHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
          {t2('tours.aboutExperience')}
        </Text>
        <Text style={[styles.description, { color: theme.textSecondary }]}>{bodyText}</Text>
      </View>
    ) : null;

  return (
    <FiltersProvider>
      <View style={[styles.root, { backgroundColor: theme.background }]}>
        <Stack.Screen options={{ headerShown: false }} />
        {tour ? (
          <TourHead
            tour={tour}
            locale={locale}
            related={related}
            relatedSeedKey={relatedSeedKey}
          />
        ) : null}
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <ScrollView
          style={{ flex: 1 }}>
          {tour ? (
            <>
              {/* One tree for both breakpoints — mobile: full-bleed hero +
                  overlapping card; desktop: filters sidebar + text-forward
                  two-column content. The regrouping is pure CSS
                  (display: contents — see detail-desktop.module.css), so the
                  title, hero, CTA and description are in the HTML once. */}
              <div className={css.shell}>
                <aside className={css.shellSidebar}>
                  {/* Desktop-only sidebar: skip the chip-scroller markup. */}
                  <FiltersBar variant="desktop" />
                </aside>
                <div className={css.shellMain}>
                  <div className={css.detail}>
                    <div className={css.detailMedia}>
                      <Hero tour={tour} title={tourTitle} theme={theme} onBack={onBack} t={t} />
                    </div>

                    <div
                      className={css.detailCard}
                      style={{ backgroundColor: theme.card, borderColor: theme.border }}>
                      <header className={css.detailHeader}>
                        <h1
                          className={css.detailTitle}
                          style={{ color: theme.text, fontFamily: Fonts?.serif }}>
                          {tourTitle}
                        </h1>
                        <MetaList tour={tour} cityName={cityName} theme={theme} t={t} />
                      </header>

                      <div
                        className={css.detailBooking}
                        style={{ backgroundColor: theme.card, borderColor: theme.border }}>
                        {isTourDelisted(tour) ? (
                          <DelistedNotice theme={theme} t={t} />
                        ) : (
                          <>
                            <PriceRow tour={tour} theme={theme} t={t} />
                            <BookButton tour={tour} theme={theme} t={t} />
                          </>
                        )}
                      </div>

                      {bodyText ? (
                        <section className={css.detailAbout}>
                          {/* Mobile separates the CTA from the body inside
                              the card; on desktop they're in different
                              columns. */}
                          <div className={css.mobileOnly}>
                            <View style={[styles.divider, { backgroundColor: theme.border }]} />
                          </div>
                          {description(t)}
                        </section>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {/* "More tours" — bracketed by data-lastmod-skip sentinels:
                  packages/web-build/src/generate-sitemap.js strips this region before hashing
                  the body, so a sibling tour changing never restamps this
                  page's sitemap lastmod. */}
              {related.length > 0 ? (
                <>
                  <div data-lastmod-skip="start" hidden />
                  <RelatedList heading={relatedHeading} seeAllHref={seeAllHref}>
                    {related.map((r) => (
                      <TourCard key={r.id} tour={r} eager={false} />
                    ))}
                  </RelatedList>
                  <div data-lastmod-skip="end" hidden />
                </>
              ) : null}
            </>
          ) : loading ? (
            <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
              {t('tours.loadingOne')}
            </Text>
          ) : error ? (
            <Text style={[styles.statusText, { color: theme.destructive }]}>
              {t('tours.loadError', { error })}
            </Text>
          ) : (
            <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
              {t('tours.notFound')}
            </Text>
          )}
          <SiteFooter />
        </ScrollView>
      </View>
    </FiltersProvider>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  statusText: {
    fontSize: 14,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.four,
  },
  heroFill: {
    width: '100%',
    height: '100%',
  },
  heroScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '40%',
  },
  backBtn: {
    position: 'absolute',
    top: Spacing.three,
    left: Spacing.three,
    width: 38,
    height: 38,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroBadge: {
    position: 'absolute',
    top: Spacing.three,
    right: Spacing.three,
  },
  metaList: {
    marginTop: Spacing.three,
    gap: Spacing.two,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  metaText: {
    fontSize: 15,
    flex: 1,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: Spacing.two,
  },
  price: {
    fontSize: 22,
    fontWeight: '700',
  },
  perPerson: {
    fontSize: 13,
  },
  delistedNotice: {
    borderRadius: Radius.lg,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    gap: 4,
  },
  delistedBanner: {
    fontSize: 15,
    fontWeight: '700',
  },
  delistedHint: {
    fontSize: 13,
  },
  bookBtn: {
    marginTop: Spacing.three,
  },
  via: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: Spacing.two,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: Spacing.four,
  },
  sectionHeading: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: Spacing.two,
  },
  description: {
    fontSize: 15,
    lineHeight: 24,
  },
});
